<template>
  <div class="password-strength">
    <div class="strength-bars">
      <div
        v-for="n in 5"
        :key="n"
        class="strength-bar"
        :class="[n <= strength ? strengthClass : '']"
      ></div>
    </div>

    <p class="strength-text">
      {{ $t("User.passwordStrength") }}: {{ strengthText }}
    </p>

    <div v-if="showRequirements" class="password-requirements">
      <ul>
        <li :class="{ fulfilled: passwordChecks.length }">
          <font-awesome-icon
            :icon="['fas', 'check-circle']"
            class="check-icon"
          />
          <span class="requirement">{{ $t("User.passwordLength") }}</span>
        </li>
        <li :class="{ fulfilled: passwordChecks.uppercase }">
          <font-awesome-icon
            :icon="['fas', 'check-circle']"
            class="check-icon"
          />
          <span class="requirement">{{ $t("User.passwordUppercase") }}</span>
        </li>
        <li :class="{ fulfilled: passwordChecks.lowercase }">
          <font-awesome-icon
            :icon="['fas', 'check-circle']"
            class="check-icon"
          />
          <span class="requirement">{{ $t("User.passwordLowercase") }}</span>
        </li>
        <li :class="{ fulfilled: passwordChecks.number }">
          <font-awesome-icon
            :icon="['fas', 'check-circle']"
            class="check-icon"
          />
          <span class="requirement">{{ $t("User.passwordNumber") }}</span>
        </li>
        <li :class="{ fulfilled: passwordChecks.special }">
          <font-awesome-icon
            :icon="['fas', 'check-circle']"
            class="check-icon"
          />
          <span class="requirement">{{ $t("User.passwordSpecial") }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: "PasswordStrengthIndicator",
  props: {
    password: {
      type: String,
      required: true,
    },
    showRequirements: {
      type: Boolean,
      required: true,
    },
  },
  computed: {
    passwordChecks() {
      return {
        length: this.password.length >= 8,
        uppercase: /[A-Z]/.test(this.password),
        lowercase: /[a-z]/.test(this.password),
        special: /[~`!@#$%^&*()\-_+={}[\]|\\;:"<>,./?]/.test(this.password),
        number: /\d/.test(this.password),
      };
    },
    strength() {
      return Object.values(this.passwordChecks).filter(Boolean).length;
    },
    strengthText() {
      const scores = ["Very Weak", "Weak", "Medium", "Strong", "Very Strong"];
      return scores[Math.min(this.strength, 4)];
    },
    strengthClass() {
      const colors = [
        "strength-1",
        "strength-2",
        "strength-3",
        "strength-4",
        "strength-5",
      ];
      return colors[Math.min(this.strength, 4)];
    },
  },
};
</script>

<style scoped>
.password-strength {
  margin-top: 10px;
}

.strength-bars {
  display: flex;
  gap: 4px;
  height: 4px;
  margin-bottom: 8px;
}

.strength-bar {
  flex: 1;
  background-color: #e5e5e5;
  border-radius: 2px;
  transition: background-color 0.2s ease;
}

.strength-1 {
  background-color: #ef4444;
}
.strength-2 {
  background-color: #f97316;
}
.strength-3 {
  background-color: #eab308;
}
.strength-4 {
  background-color: #22c55e;
}
.strength-5 {
  background-color: #16a34a;
}

.strength-text {
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
}

.password-requirements {
  background-color: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px;
  margin-top: 8px;
}

.password-requirements ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.password-requirements li {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  font-size: 14px;
  color: #666;
}

.password-requirements li:last-child {
  margin-bottom: 0;
}

.check-icon {
  margin-right: 8px;
  color: #ccc;
}

.fulfilled {
  color: #22c55e !important;
}

.fulfilled .check-icon {
  color: #22c55e;
}

@media (max-width: 767px) {
  .password-form {
    gap: 10px;
  }
  .password-requirements li {
    font-size: 10px;
  }
  .strength-text {
    font-size: 10px;
  }
}
</style>
